import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { changeWorksheet } from 'state/actions/action-creators';
import { translate } from 'translations/translator';
import { Box, Card, Switch, FormControlLabel } from '@mui/material';
import { makeStyles } from '@mui/styles';
import landscapePath from 'assets/photo/worksheet-maker/previews/landscape.png';
import portraitPath from 'assets/photo/worksheet-maker/previews/portrait.png';
import getS3AssetUrl from 'utils/get-s3-asset-url';
const t = key => translate('worksheet', key);

const TemplateSelector = ({ menuSectionId, language, handleToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const section = useSelector((state: any) => {
    return state.worksheetMaker.sections[menuSectionId];
  });
  const items = section.worksheetList;
  const classes = useStyles();
  const { content, title, templates, worksheetItem, worksheetTitle, worksheetImage } = classes;

  const onSelectWorksheet = item => {
    dispatch(changeWorksheet(item.id));

    const route = `/${menuSectionId}/${item.id}${location.search}`;

    navigate(route);
  };

  const handleKeyPress = (event, item) => {
    if (event.key === 'Enter') {
      onSelectWorksheet(item);
    }
  };

  const renderItem = (item, index) => {
    let thumbnailPath;
    let defaultPath = item.pageLayout === 'portrait' ? portraitPath : landscapePath;

    if (item.thumbnailImage) {
      thumbnailPath = getS3AssetUrl('worksheet-maker/previews/' + item.thumbnailImage);
    } else {
      thumbnailPath = defaultPath;
    }

    return (
      <Card
        className={worksheetItem}
        elevation={2}
        role="button"
        key={index}
        tabIndex={0}
        onClick={() => onSelectWorksheet(item)}
        onKeyPress={event => handleKeyPress(event, item)}
      >
        <div className={worksheetTitle}>{t(item.sheetName)}</div>
        <img className={worksheetImage} src={thumbnailPath} alt={`${item.sheetName} worksheet`} />
      </Card>
    );
  };

  const renderItems = () => {
    return items.map((item, index) => {
      return renderItem(item, index);
    });
  };

  return (
    <Box
      className={content}
      id={`${t(section.name)} Tab Panel`.replace(/ /g, '-')}
      role="tabpanel"
      aria-labelledby={`${t(section.name)} Tab`.replace(/ /g, '-')}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          '@media (max-width: 600px)': { flexDirection: 'column', padding: '0 0 0 10px' }
        }}
      >
        <h3 className={title}>{t('selectWorksheet')}</h3>

        <span style={{ display: 'flex' }}>
          <FormControlLabel
            sx={{
              height: 'max-content',
              '@media (max-width: 400px)': {
                flexDirection: 'column',
                '& .MuiFormControlLabel-label': {
                  fontSize: '8px'
                }
              }
            }}
            control={
              <Switch
                defaultChecked
                disableRipple
                checked={language === 'ES_US'}
                onChange={handleToggle}
                inputProps={{ 'aria-label': t('switchLanguage') }}
                color="primary"
                sx={{
                  '& .MuiSwitch-input:focus + .MuiSwitch-thumb': { outline: '3px solid #005fcc' }
                }}
              />
            }
            label="Español"
          />
        </span>
      </Box>
      <Box className={templates}>{renderItems()}</Box>
    </Box>
  );
};

export default TemplateSelector;

const useStyles = makeStyles({
  content: {
    width: '100%',
    height: '100%',
    overflowY: 'auto'
  },
  title: {
    fontFamily: 'Futura-Bold',
    fontSize: '32px',
    marginTop: '30px',
    marginLeft: '20px',
    marginBottom: '0px',
    '@media (max-width: 600px)': {
      fontSize: '20px',
      marginTop: '10px',
      marginLeft: '10px'
    },
    '@media (max-width: 400px)': {
      fontSize: '10px',
      marginTop: '15px'
    }
  },
  templates: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  worksheetItem: {
    display: 'flex',
    justifyContent: 'center',
    margin: '11px',
    color: 'black',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px',
    position: 'relative',
    minWidth: '30%',
    '&:hover': {
      backgroundColor: '#EEEEEE',
      borderRadius: '10px',
      cursor: 'pointer'
    }
  },
  worksheetTitle: {
    fontFamily: 'Futura-Semibold',
    fontSize: '12px',
    textAlign: 'center',
    width: '150px',
    minHeight: '40px',
    display: 'flex',
    flexDirection: 'column-reverse',
    marginBottom: '5px'
  },
  worksheetImage: {
    width: '150px',
    height: 'auto',
    display: 'block',
    border: '1px solid #cccccc'
  }
});
